/* eslint-disable local/no-small-variable-names */
import * as React from 'react';

import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex whitespace-nowrap items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        beginner:
          'border-green-200 bg-green-500/10 text-green-700 dark:border-green-800/30 dark:bg-green-950/20 dark:text-green-300',
        intermediate:
          'border-orange-200 bg-orange-500/10 text-orange-700 dark:border-orange-800/30 dark:bg-orange-950/20 dark:text-orange-300',
        advanced:
          'border-red-200 bg-red-500/10 text-red-700 dark:border-red-800/30 dark:bg-red-950/20 dark:text-red-300',
        active:
          'border-blue-200 bg-blue-500/10 text-blue-700 dark:border-blue-800/30 dark:bg-blue-950/20 dark:text-blue-300',
        inactive:
          'border-gray-200 bg-gray-500/10 text-gray-700 dark:border-gray-800/30 dark:bg-gray-950/20 dark:text-gray-300',
        completed:
          'border-success bg-success/10 text-success dark:border-success/30 dark:bg-success/10 dark:text-success',
        pending:
          'border-yellow-200 bg-yellow-500/10 text-yellow-700 dark:border-yellow-800/30 dark:bg-yellow-950/20 dark:text-yellow-300',
        tool: 'border-purple-200 bg-purple-500/10 text-purple-700 dark:border-purple-800/30 dark:bg-purple-950/20 dark:text-purple-300',
        assessment:
          'border-emerald-200 bg-emerald-500/10 text-emerald-700 dark:border-emerald-800/30 dark:bg-emerald-950/20 dark:text-emerald-300',
        jtbd: 'border-purple-200 bg-purple-500/10 text-purple-700 dark:border-purple-800/30 dark:bg-purple-950/20 dark:text-purple-300',
        general:
          'border-slate-200 bg-slate-500/10 text-slate-700 dark:border-slate-800/30 dark:bg-slate-950/20 dark:text-slate-300',
        ai: 'border-blue-200 bg-blue-500/10 text-blue-700 dark:border-blue-800/30 dark:bg-blue-950/20 dark:text-blue-300',
        high: 'border-red-200 bg-red-500/10 text-red-700 dark:border-red-800/30 dark:bg-red-950/20 dark:text-red-300',
        medium:
          'border-yellow-200 bg-yellow-500/10 text-yellow-700 dark:border-yellow-800/30 dark:bg-yellow-950/20 dark:text-yellow-300',
        low: 'border-green-200 bg-green-500/10 text-green-700 dark:border-green-800/30 dark:bg-green-950/20 dark:text-green-300',
        'needs-testing':
          'border-gray-200 bg-gray-500/10 text-gray-700 dark:border-gray-800/30 dark:bg-gray-950/20 dark:text-gray-300',
        proven:
          'border-green-200 bg-green-500/10 text-green-700 dark:border-green-800/30 dark:bg-green-950/20 dark:text-green-300',
        disproven:
          'border-red-200 bg-red-500/10 text-red-700 dark:border-red-800/30 dark:bg-red-950/20 dark:text-red-300',
      },
      size: {
        default: 'px-2.5 py-0.5 text-xs',
        sm: 'px-2 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const capitalizeWords = (text: string): string => {
  return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  children: React.ReactNode;
}

export const Badge = ({
  className,
  variant,
  size,
  children,
  ...props
}: BadgeProps) => {
  const displayText =
    typeof children === 'string' ? capitalizeWords(children) : children;

  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {displayText}
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export { badgeVariants };
